/**
 * The central triage vocabulary for tasks entering the Atlas Product Board's Inbound. Shared by
 * the server workflow that scores a task and the board UI that shows the verdict.
 */

/** Whether to build the ask at all — decided before any routing. */
export enum TriageDisposition {
  BuildAsAsked = 'build_as_asked',
  BuildDifferently = 'build_differently',
  FoldIntoTheme = 'fold_into_theme',
  DontBuild = 'dont_build',
  Discovery = 'discovery',
}

export enum TriagePriority {
  P0 = 'P0',
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3',
}

/** Where a triaged task goes next. Stored on the board's "Lane" field and on `task_triage`. */
export enum TriageLane {
  FastLane = 'fast_lane',
  Ready = 'ready',
  UpNext = 'up_next',
  NeedsInput = 'needs_input',
  PmReview = 'pm_review',
  FoldIntoTheme = 'fold_into_theme',
  Duplicate = 'duplicate',
  ByDesign = 'by_design',
  Backlog = 'backlog',
}

/** Lanes Arc applies on its own; every other lane waits for a person's reaction. */
export const AUTO_APPLIED_LANES: ReadonlySet<TriageLane> = new Set([
  TriageLane.NeedsInput,
  TriageLane.PmReview,
  TriageLane.FoldIntoTheme,
])

/**
 * Proposals that move or close the task rather than groom it. Like every lane but the three
 * above, they wait in Inbound with the Lane field set until a person approves.
 */
export const SETTLING_LANES: ReadonlySet<TriageLane> = new Set([
  TriageLane.Backlog,
  TriageLane.Duplicate,
  TriageLane.ByDesign,
])

/** Lanes triage has judged buildable: the plan's groomed ticket is written at once, and approval only moves it. */
export const GROOMED_LANES: ReadonlySet<TriageLane> = new Set([
  TriageLane.FastLane,
  TriageLane.Ready,
  TriageLane.UpNext,
])

/** The board's Lane field options, matched by name: create the field with exactly these. */
export const TRIAGE_LANE_OPTION_NAMES: Record<TriageLane, string> = {
  [TriageLane.FastLane]: 'Fast lane',
  [TriageLane.Ready]: 'Ready',
  [TriageLane.UpNext]: 'Up next',
  [TriageLane.NeedsInput]: 'Needs input',
  [TriageLane.PmReview]: 'PM review',
  [TriageLane.FoldIntoTheme]: 'Fold into theme',
  [TriageLane.Duplicate]: 'Duplicate',
  [TriageLane.ByDesign]: 'By design',
  [TriageLane.Backlog]: 'Backlog',
}

export enum TriageStatus {
  /** The job is running: investigate → plan → synthesis. */
  Running = 'running',
  /** Scored; waiting on a 👍 / "go" for a proposed lane, or already applied for an auto lane. */
  Proposed = 'proposed',
  Applied = 'applied',
  /** The run failed before a verdict; the task stays where it was. */
  Failed = 'failed',
  /** Every code-agent slot was busy, so the pass never started; the sweep queues it again. */
  Deferred = 'deferred',
  /** A newer triage replaced this one (re-triage). */
  Superseded = 'superseded',
}

/** The engineering board triage, feedback and error filing all work on. Created by hand, found by name. */
export const ATLAS_PRODUCT_BOARD_PROJECT_NAME = 'Atlas Product Board'

/** Board field names the triage writes to. Created by hand on the board, found by name. */
export const TRIAGE_BOARD_FIELD_NAMES = {
  lane: 'Lane',
  initiative: 'Initiative',
  type: 'Type',
  priority: 'Priority',
  storyPoints: 'Story Points',
  productArea: 'Product Area',
  stakeholders: 'Stakeholders',
} as const

/** The board sections the lanes map onto. */
export const TRIAGE_SECTION_NAMES = {
  inbound: 'Inbound',
  backlog: 'Backlog',
  ready: 'Ready',
  readyQuickTasks: 'Ready - Quick Tasks',
  readyFeatures: 'Ready - Features',
  upNext: 'Up Next',
  inProgress: 'In Progress',
} as const

export interface ITriageEvidence {
  path: string
  line: number | null
  why: string
}

export interface ITriageOpenQuestion {
  question: string
  lean: string
  confidence: 'high' | 'medium' | 'low'
}

export interface ITriageDuplicateCandidate {
  taskId: string
  title: string
  similarity: number
  why: string
}

/** The verdict in full, as stored in `task_triage.verdict` and rendered into the triage comment. */
export interface ITriageVerdict {
  disposition: TriageDisposition
  dispositionReason: string
  /** For build_differently: the alternative, costed next to the ask. */
  alternative: { summary: string; effort: string; why: string } | null
  intent: string
  priority: TriagePriority
  priorityReason: string
  effort: string
  autobuild: number
  skipScore: number
  lane: TriageLane
  laneRule: string
  /** Why a developer builds it rather than Dwight Bot (never a reason for PM review). */
  holdReasons: string[]
  /** The groomed title the plan proposes; applied on approval. */
  fixTitle: string | null
  /** Files the change touches, best first. */
  files: string[]
  /** Questions about intent the model couldn't guess; they put the task in Needs Input. */
  questions: ITriageOpenQuestion[]
  /** Questions it could answer itself, shown as assumptions the requester can correct. */
  assumptions: ITriageOpenQuestion[]
  /** Who the assumptions were addressed to; a reply from one of them re-triages. */
  stakeholderUserIds?: string[]
  /** The title or body was edited while the pass ran, so the pass did not groom over it. */
  keptEditedText?: boolean
  duplicateOf: ITriageDuplicateCandidate | null
  alreadyFixedReference: string | null
  themeTaskId: string | null
  themeTitle: string | null
  initiative: string | null
  goalFit: string | null
  productArea: string | null
  evidence: ITriageEvidence[]
  summary: string
  /** For dont_build: the reply the PM would send the requester. */
  suggestedReply: string | null
}
